import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import {
  ChangeDetectorRef, Component, DoCheck, Input, OnInit, Self, ViewChild
} from '@angular/core';
import {
  AbstractControl, ControlValueAccessor, UntypedFormBuilder, UntypedFormGroup, NgControl, ValidationErrors,
  Validator, Validators
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';

import {
  AddOrUpdateFieldResponseDialog
} from 'app/field-shared/dialogs/add-or-update-field-response/add-or-update-field-response.dialog';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { isNilOrEmpty } from 'helpers';

import { SearchTags, TagCategoryInfoFragment } from 'generated/graphql';
import { ApplyTagFormModel } from 'field-shared/ApplyTagFormModel';

@Component({
  selector: 'app-apply-tag-form',
  templateUrl: './apply-tag-form.component.html'
})
export class ApplyTagFormComponent extends WithDisposableSubject()
  implements ControlValueAccessor, OnInit, DoCheck, Validator {

  // <Overlay Related>
  /** Is the select tag dropdown open? */
  isOpen = false;

  /** Reference to trigger to open the select
   *  (Used to dynamically set initial width)
   */
  @ViewChild(CdkOverlayOrigin) trigger: CdkOverlayOrigin;

  /**
   *  Use observable to get the trigger width to avoid a
   *  ExpressionChangedAfterItHasBeenCheckedError
   */
  private _triggerWidth = new Subject<string>();

  /**
   *  Use observable to get the trigger width to avoid a
   *  ExpressionChangedAfterItHasBeenCheckedError
   */
  triggerWidth$ = this._triggerWidth.asObservable().pipe(
    takeUntil(this.disposer)
  );

  // </Overlay Related>

  /**
   * Disable autofocus
   */
  @Input() autoFocusDisabled = false;

  /** Is form required */
  @Input() required = true;

  /**
   * Required input of the tag category
   */
  @Input() tagCategory: TagCategoryInfoFragment;

  /**
   * Required input for campaign id
   */
  @Input() campaignId: string;

  /**
   * If true, attachment input is hidden
   */
  @Input() hideAttachments: boolean;

  /** Internal form group */
  formGroup: UntypedFormGroup;

  get selectedTag() {
    return this.formGroup.get('selectedTag')?.value;
  }

  get selectedTagControl() {
    return this.formGroup.get('selectedTag');
  }

  get noteControl() {
    return this.formGroup.get('note');
  }

  get shiftControl() {
    return this.formGroup.get('shiftInfo');
  }

  get fileControl() {
    return this.formGroup.get('file');
  }


  private isTouched = false;

  _onTouched = () => { };
  _onChanged = () => { };

  /**
   *
   */
  constructor(
    private _fb: UntypedFormBuilder,
    private _cd: ChangeDetectorRef,
    private _dialog: MatDialog,
    @Self() public controlDir: NgControl

  ) {
    super();
    this.controlDir.valueAccessor = this;

    const fg = this._fb.group({
      selectedTag: [null, Validators.required],
      note: null,
      shiftInfo: null,
      file: null
    });

    this.formGroup = fg;
  }

  getMinRole(tagCategory: TagCategoryInfoFragment) {
    return tagCategory.readonly ? 'administrator' : 'activist';
  }

  openOverlay() {
    const width = this.trigger?.elementRef?.nativeElement?.offsetWidth;
    this._triggerWidth.next(width ? `${width}px` : '100%');
    this.isOpen = true;
    this._cd.markForCheck();
  }

  closeOverlay() {
    this.isOpen = false;
    this._cd.markForCheck();
  }

  onAddNewResponseClick() {
    this.isOpen = false;
    AddOrUpdateFieldResponseDialog.open(this._dialog, {
      data: {
        tagCategoryId: this.tagCategory.id,
        tagType: this.tagCategory.allowToCreateTagType,
        tag: null,
        campaignId: this.campaignId,
        isUniversal: this.tagCategory.isUniversal
      }
    }).afterClosed().subscribe((createdTag) => {
      if (!isNilOrEmpty(createdTag)) {
        this.onTagSelected(createdTag as SearchTags.Node);
      }
    });
  }

  onTagSelected(tag: SearchTags.Node) {
    this.selectedTagControl.setValue(tag);
    this.isTouched = true;
    this.noteControl.updateValueAndValidity();
    this._onTouched();
    this.isOpen = false;
    this._cd.markForCheck();
  }

  setFile(file: File, control: AbstractControl) {
    control.setValue(file);
    this._cd.markForCheck();
  }

  // https://stackblitz.com/edit/angular-eqntdj?file=src%2Fapp%2Fmy-custom-control.component.ts
  ngOnInit() {
    // NOTE: Have to bind to `this` to set the validator for the incoming NgControl
    this.controlDir.control.setValidators(this.validate.bind(this));

    // In cases where the tag form is optional.
    // Remove the validator which requires a tag.
    if (this.required === false) {
      this.selectedTagControl.removeValidators(Validators.required);
      this.selectedTagControl.updateValueAndValidity();
    }

    setTimeout(() => this.controlDir.control.updateValueAndValidity());
  }

  ngDoCheck() {
    if (this.controlDir.touched && !this.isTouched) {
      this.isTouched = true;
      this.formGroup.markAllAsTouched();
      this._cd.markForCheck();
    }
  }

  writeValue(obj: ApplyTagFormModel): void {
    if (obj) {
      this.formGroup.patchValue(obj, { emitEvent: false });
    }
    if (obj == null) {
      this.formGroup.reset({ value: null });
    }
  }

  registerOnChange(fn: any): void {
    this._onChanged = fn;
    this.formGroup.valueChanges.pipe(
      takeUntil(this.disposer),
    ).subscribe(this._onChanged);
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.formGroup.disable();
    } else {
      this.formGroup.enable();
    }
  }

  /** Validator Implementation */
  validate(control: AbstractControl): ValidationErrors {
    if (this.formGroup.valid) {
      return null;
    }

    const aggErrors = Object.values(this.formGroup.controls)
      .map(ctr => ctr.errors)
      .filter(errs => !isNilOrEmpty(errs));

    return Object.assign({}, ...aggErrors);
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.formGroup.valueChanges.pipe(
      startWith(this.formGroup.value),
      takeUntil(this.disposer)
    ).subscribe((_) => {
      fn();
    });
  }

}
